import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_PATHS } from '../config/api-paths';
import { LenderOffersApi } from '../tokens/lender-offers.token';
import {
  AuctionRankingView,
  LenderOfferPlacement,
  LenderOfferView,
  LenderPassRequest,
  LenderPassResult,
  OfferCounterResponse,
} from '../models/lender-origination.model';
import { AuctionView } from '../models/financing-servicing.model';

/**
 * REST-only implementation of {@link LenderOffersApi} (EM-699 / F3 — EM-708).
 *
 * Calls the B1 `LenderOfferResource` under `/services/embedded/api/v1`:
 *   GET   /lender/offers?stage=&status=&product=
 *   POST  /lender/offers                                            (Idempotency-Key header)
 *   PATCH /lender/offers/{offerId}
 *   GET   /auctions/{auctionId}/ranking
 *   GET   /lender/auctions/{auctionId}                              (EM-1778 — the lender's own read)
 *   POST  /lender/auctions/{auctionId}/pass                         (EM-1779 — BRS §4.6.3 Pass)
 *   POST  /auctions/{auctionId}/offers/{offerId}/counter-response
 *
 * Rides the Keycloak bearer (authInterceptor). Rule 07 / Rule 10 — no Mock; Playwright via
 * `page.route()`. Rule 09 — the raw `HttpErrorResponse` propagates for surfacing.
 */
@Injectable()
export class LenderOffersRestService implements LenderOffersApi {
  private readonly http = inject(HttpClient);
  private readonly offersBase = API_PATHS.LENDER_OFFERS;
  private readonly auctionsBase = API_PATHS.AUCTIONS;
  /** EM-1778 — the LENDER's auction edge; NOT `AUCTIONS`, which is SME-scoped and 404s a lender. */
  private readonly lenderAuctionsBase = API_PATHS.LENDER_AUCTION_EDGE;

  listMyOffers(filters?: { stage?: string; status?: string; product?: string }): Observable<LenderOfferView[]> {
    let params = new HttpParams();
    if (filters?.stage) {
      params = params.set('stage', filters.stage);
    }
    if (filters?.status) {
      params = params.set('status', filters.status);
    }
    if (filters?.product) {
      params = params.set('product', filters.product);
    }
    return this.http.get<LenderOfferView[]>(this.offersBase, { params });
  }

  placeOffer(request: LenderOfferPlacement, idempotencyKey?: string): Observable<LenderOfferView> {
    let headers = new HttpHeaders();
    if (idempotencyKey) {
      headers = headers.set('Idempotency-Key', idempotencyKey);
    }
    return this.http.post<LenderOfferView>(this.offersBase, request, { headers });
  }

  reviseOffer(offerId: number, request: LenderOfferPlacement): Observable<LenderOfferView> {
    return this.http.patch<LenderOfferView>(`${this.offersBase}/${offerId}`, request);
  }

  ranking(auctionId: number): Observable<AuctionRankingView> {
    return this.http.get<AuctionRankingView>(`${this.auctionsBase}/${auctionId}/ranking`);
  }

  auctionView(auctionId: number): Observable<AuctionView> {
    return this.http.get<AuctionView>(`${this.lenderAuctionsBase}/${auctionId}`);
  }

  pass(auctionId: number, request?: LenderPassRequest): Observable<LenderPassResult> {
    return this.http.post<LenderPassResult>(`${this.lenderAuctionsBase}/${auctionId}/pass`, request ?? {});
  }

  counterResponse(auctionId: number, offerId: number, request: OfferCounterResponse): Observable<LenderOfferView> {
    return this.http.post<LenderOfferView>(
      `${this.auctionsBase}/${auctionId}/offers/${offerId}/counter-response`,
      request,
    );
  }
}
